// pages/strategic-playbook/[id].js
// ════════════════════════════════════════════════════
// STRATEGIC PLAYBOOK — Dossier Reader (v1.5)
// ════════════════════════════════════════════════════
// Two reading modes:
//   1. Long-form scroll (default) — all 13 sections at once
//   2. Chunked progression — one section at a time with
//      prev/next nav. Forces 5-10 minute focused reads
//      (matches Tim's stated reading pattern).
//
// Mode preference saved to localStorage.
// ════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { DOSSIERS, getDossier } from '../../data/strategic-playbook/dossiers';
import { getCascadeForDossier } from '../../data/strategic-playbook/cascade-dossiers';

const STORAGE_KEY = 'sp-reader-mode';

export default function DossierReader() {
  const router = useRouter();
  const { id } = router.query;
  const dossier = id ? getDossier(id) : null;
  const cascade = dossier ? getCascadeForDossier(dossier.id) : null;

  const [mode, setMode] = useState('scroll'); // 'scroll' | 'chunked'
  const [sectionIndex, setSectionIndex] = useState(0);

  // Load preferred mode from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'chunked' || saved === 'scroll') {
      setMode(saved);
    }
  }, []);

  // Save mode preference + reset section index when mode changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Reset section index when navigating to a different dossier
  useEffect(() => {
    setSectionIndex(0);
  }, [id]);

  if (!id) {
    return (
      <div className="sp-root">
        <div className="sp-page">Loading...</div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="sp-root">
        <div className="sp-page">
          <Link href="/strategic-playbook/library" className="sp-reader-back">
            ← Back to Library
          </Link>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', color: 'var(--sp-warm-cream)' }}>
            Dossier not found
          </h1>
          <p>The dossier &ldquo;{id}&rdquo; doesn&apos;t exist in the v1.0 library.</p>
        </div>
      </div>
    );
  }

  const totalSections = dossier.sections.length;
  const currentSection = dossier.sections[sectionIndex];

  return (
    <div className="sp-root">
      <Head>
        <title>{dossier.title} · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <div className="sp-reader">
          {/* Top nav row — back-links + mode toggle */}
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: 24,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Link href="/strategic-playbook" className="sp-reader-back" style={{ marginBottom: 0 }}>
              ← Dashboard
            </Link>
            <Link
              href={
                dossier.type === 'thinker'
                  ? '/strategic-playbook/library?type=thinker'
                  : dossier.type === 'shock-playbook'
                    ? '/strategic-playbook/library?type=shock-playbook'
                    : dossier.type === 'tectonic'
                      ? '/strategic-playbook/library?type=tectonic'
                      : '/strategic-playbook/library'
              }
              className="sp-reader-back"
              style={{ marginBottom: 0 }}
            >
              ← Library
            </Link>
            {cascade && (
              <Link
                href={`/strategic-playbook/cascade/${cascade.id}`}
                style={{
                  marginBottom: 0,
                  padding: '6px 14px',
                  background: 'var(--sp-brass-bg)',
                  border: '1px solid var(--sp-aged-brass)',
                  borderRadius: 'var(--sp-radius)',
                  color: 'var(--sp-aged-brass)',
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  textDecoration: 'none',
                }}
              >
                Cascade Dossier →
              </Link>
            )}
            <div style={{ flex: 1 }} />
            <ModeToggle mode={mode} onChange={setMode} />
          </div>

          {/* Header — always shown in both modes */}
          <div className="sp-reader-header">
            <div className="sp-reader-eyebrow">{dossier.eyebrow}</div>
            <h1 className="sp-reader-title">{dossier.title}</h1>
            <div className="sp-reader-meta">
              {dossier.pillars.map((p, i) => (
                <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>
                  P{p} · {dossier.pillarNames[i]}
                </span>
              ))}
            </div>
            <div className="sp-reader-meta-row">
              <span>{dossier.readingTimeMin} min read</span>
              <span>·</span>
              <span>
                Confidence{' '}
                <span style={{ color: 'var(--sp-aged-brass)' }}>{dossier.confidence}/100</span>
              </span>
              <span>·</span>
              <span>Reversibility: {dossier.reversibility}</span>
              <span>·</span>
              <span>Recovery: {dossier.timeToRecoverMonths} months</span>
            </div>
            <div
              style={{
                marginTop: 24,
                padding: 18,
                background: 'var(--sp-parchment-bg)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                fontSize: 13,
                lineHeight: 1.7,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 10,
                }}
              >
                Applies / Does Not Apply
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: 'var(--sp-forest-moss)' }}>Applies when:</strong>{' '}
                {dossier.appliesWhen}
              </div>
              <div>
                <strong style={{ color: 'var(--sp-oxblood)' }}>Does NOT apply when:</strong>{' '}
                {dossier.doesNotApplyWhen}
              </div>
            </div>
          </div>

          {mode === 'scroll' ? (
            <>
              {renderPhases(dossier.sections)}
              <div className="sp-footer-mark">
                — end of dossier · {dossier.readingTimeMin} minutes —
              </div>
              <RelatedDossiers currentId={dossier.id} />
            </>
          ) : (
            <ChunkedReader
              dossier={dossier}
              sectionIndex={sectionIndex}
              setSectionIndex={setSectionIndex}
              totalSections={totalSections}
              currentSection={currentSection}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mode Toggle ────────────────────────────────────
function ModeToggle({ mode, onChange }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--sp-soft-charcoal)',
        border: '1px solid var(--sp-divider)',
        borderRadius: 'var(--sp-radius)',
        padding: 2,
        fontFamily: 'var(--sp-font-mono)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.10em',
      }}
    >
      <button
        onClick={() => onChange('scroll')}
        style={{
          padding: '6px 12px',
          background: mode === 'scroll' ? 'var(--sp-brass-bg)' : 'transparent',
          color: mode === 'scroll' ? 'var(--sp-aged-brass)' : 'rgba(232,221,196,0.55)',
          border: `1px solid ${mode === 'scroll' ? 'var(--sp-aged-brass)' : 'transparent'}`,
          borderRadius: 'var(--sp-radius)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
        }}
      >
        Long-form
      </button>
      <button
        onClick={() => onChange('chunked')}
        style={{
          padding: '6px 12px',
          background: mode === 'chunked' ? 'var(--sp-brass-bg)' : 'transparent',
          color: mode === 'chunked' ? 'var(--sp-aged-brass)' : 'rgba(232,221,196,0.55)',
          border: `1px solid ${mode === 'chunked' ? 'var(--sp-aged-brass)' : 'transparent'}`,
          borderRadius: 'var(--sp-radius)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
        }}
      >
        One Section
      </button>
    </div>
  );
}

// ─── Chunked Reader ─────────────────────────────────
function ChunkedReader({
  dossier,
  sectionIndex,
  setSectionIndex,
  totalSections,
  currentSection,
}) {
  const isFirst = sectionIndex === 0;
  const isLast = sectionIndex === totalSections - 1;

  // Get current phase label (find the most recent phaseLabel at or before current index)
  const phaseLabel = (() => {
    for (let i = sectionIndex; i >= 0; i--) {
      if (dossier.sections[i].phaseLabel) return dossier.sections[i].phaseLabel;
    }
    return '';
  })();

  function goPrev() {
    if (!isFirst) {
      setSectionIndex(sectionIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function goNext() {
    if (!isLast) {
      setSectionIndex(sectionIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  return (
    <div style={{ marginTop: 24 }}>
      {/* Progress + phase indicator */}
      <div
        style={{
          marginBottom: 24,
          padding: '14px 20px',
          background: 'var(--sp-soft-charcoal)',
          border: '1px solid var(--sp-divider)',
          borderRadius: 'var(--sp-radius-card)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: 'var(--sp-aged-brass)',
            }}
          >
            {phaseLabel || `Section ${sectionIndex + 1}`}
          </div>
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 11,
              color: 'rgba(232, 221, 196, 0.55)',
              letterSpacing: '0.06em',
            }}
          >
            {sectionIndex + 1} of {totalSections}
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 4,
            background: 'rgba(232, 221, 196, 0.10)',
            borderRadius: 'var(--sp-radius-pill)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${((sectionIndex + 1) / totalSections) * 100}%`,
              background: 'var(--sp-aged-brass)',
              borderRadius: 'var(--sp-radius-pill)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Single section card — taller padding, larger type for focused reading */}
      <div
        style={{
          padding: '32px 36px',
          background: 'var(--sp-soft-charcoal)',
          border: '1px solid var(--sp-divider)',
          borderRadius: 'var(--sp-radius-card)',
          minHeight: 400,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--sp-font-mono)',
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            color: 'rgba(232, 221, 196, 0.50)',
            marginBottom: 12,
          }}
        >
          Section {currentSection.num}
        </div>
        <h2
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontWeight: 500,
            fontSize: 26,
            lineHeight: 1.25,
            color: 'var(--sp-warm-cream)',
            margin: '0 0 22px 0',
            letterSpacing: '-0.005em',
          }}
        >
          {currentSection.title}
        </h2>
        <div
          className="sp-section-body"
          style={{ fontSize: 16, lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{ __html: currentSection.body }}
        />
      </div>

      {/* Prev/Next nav */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 24,
          alignItems: 'center',
        }}
      >
        <button
          onClick={goPrev}
          disabled={isFirst}
          style={{
            padding: '12px 20px',
            background: isFirst ? 'transparent' : 'var(--sp-soft-charcoal)',
            border: `1px solid ${isFirst ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
            borderRadius: 'var(--sp-radius-card)',
            color: isFirst ? 'rgba(232, 221, 196, 0.30)' : 'var(--sp-aged-brass)',
            fontFamily: 'var(--sp-font-mono)',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            cursor: isFirst ? 'not-allowed' : 'pointer',
          }}
        >
          ← Previous
        </button>

        <div style={{ flex: 1, textAlign: 'center' }}>
          <span
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              color: 'rgba(232, 221, 196, 0.45)',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
            }}
          >
            {isLast ? 'End of dossier' : `${totalSections - sectionIndex - 1} sections left`}
          </span>
        </div>

        {isLast ? (
          <Link
            href={`/strategic-playbook/library`}
            style={{
              padding: '12px 20px',
              background: 'var(--sp-brass-bg)',
              border: '1px solid var(--sp-aged-brass)',
              borderRadius: 'var(--sp-radius-card)',
              color: 'var(--sp-aged-brass)',
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              textDecoration: 'none',
            }}
          >
            Back to Library →
          </Link>
        ) : (
          <button
            onClick={goNext}
            style={{
              padding: '12px 20px',
              background: 'var(--sp-brass-bg)',
              border: '1px solid var(--sp-aged-brass)',
              borderRadius: 'var(--sp-radius-card)',
              color: 'var(--sp-aged-brass)',
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              cursor: 'pointer',
            }}
          >
            Next →
          </button>
        )}
      </div>

      {/* Section TOC strip — clickable jumps */}
      <div
        style={{
          marginTop: 32,
          paddingTop: 20,
          borderTop: '1px solid var(--sp-divider)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--sp-font-mono)',
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            color: 'rgba(232, 221, 196, 0.45)',
            marginBottom: 12,
          }}
        >
          Jump to section
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {dossier.sections.map((s, i) => (
            <button
              key={s.num}
              onClick={() => {
                setSectionIndex(i);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                width: 32,
                height: 32,
                background: i === sectionIndex
                  ? 'var(--sp-aged-brass)'
                  : i < sectionIndex
                  ? 'rgba(156, 123, 63, 0.30)'
                  : 'var(--sp-soft-charcoal)',
                border: `1px solid ${i === sectionIndex ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                borderRadius: 'var(--sp-radius)',
                color: i === sectionIndex
                  ? 'var(--sp-charcoal-black)'
                  : i < sectionIndex
                  ? 'var(--sp-aged-brass)'
                  : 'rgba(232, 221, 196, 0.55)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              title={s.title}
            >
              {s.num}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Long-form rendering ────────────────────────────
function renderPhases(sections) {
  const phases = [];
  let currentPhase = null;

  sections.forEach((s) => {
    if (s.phaseLabel) {
      currentPhase = { label: s.phaseLabel, sections: [] };
      phases.push(currentPhase);
    }
    if (currentPhase) {
      currentPhase.sections.push(s);
    }
  });

  return phases.map((phase, pi) => (
    <div key={pi} className="sp-phase">
      <div className="sp-phase-label">{phase.label}</div>
      {phase.sections.map((s) => (
        <Section key={s.num} section={s} />
      ))}
    </div>
  ));
}

function Section({ section }) {
  return (
    <div className="sp-section">
      <div className="sp-section-num">Section {section.num}</div>
      <h2 className="sp-section-title">{section.title}</h2>
      <div
        className="sp-section-body"
        dangerouslySetInnerHTML={{ __html: section.body }}
      />
    </div>
  );
}

function RelatedDossiers({ currentId }) {
  const others = DOSSIERS.filter((d) => d.id !== currentId).slice(0, 2);
  if (others.length === 0) return null;

  return (
    <div style={{ marginTop: 48 }}>
      <div
        style={{
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.20em',
          color: 'var(--sp-aged-brass)',
          marginBottom: 16,
        }}
      >
        Related Dossiers
      </div>
      <div className="sp-grid-2-eq">
        {others.map((d) => (
          <Link
            key={d.id}
            href={`/strategic-playbook/${d.id}`}
            className="sp-dossier-card"
          >
            <div className="sp-dossier-card-eyebrow">{d.eyebrow}</div>
            <h3 className="sp-dossier-card-title">{d.title}</h3>
            <p className="sp-dossier-card-summary">{d.summary.slice(0, 140)}...</p>
            <div className="sp-dossier-card-meta">
              <span className={`sp-tag sp-pillar-bg-${d.primaryPillar} sp-pillar-${d.primaryPillar}`}>
                P{d.primaryPillar}
              </span>
              <span className="sp-dossier-card-confidence">
                {d.readingTimeMin} min · {d.confidence}/100
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
